import { filter, find, forEach, get, isEmpty, isNil, join, isEqual } from 'lodash';

/**
 * Gets Template Id.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {string} The annotation template id or an empty string if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getId = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, '_id', '');
};

/**
 * Gets Template Name.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {string} The annotation template name or an empty string if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getName = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'name', '');
};

/**
 * Gets Template creation time.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {string} The annotation template creation time or an empty string if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getCreation = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'createdAt', '');
};

/**
 * Gets Template last update time.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {string} The annotation template last update time or an empty string if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getLastUpdate = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'updatedAt', '');
};

/**
 * Checks if Template is deleted.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {boolean} True if annotation template is deleted, false otherwise.
 * @throws Error if annotation template object is undefined.
 */
export const isDeleted = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'deleted', '') !== 'no';
};

/**
 * Gets Template Icon.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {string} The annotation template icon or an empty string if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getIcon = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'icon', 'apple-alt');
};

/**
 * Gets Template library of icon.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {string} The annotation template icon or an empty string if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getLibraryIcon = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'libraryIcon', 'fontawesome');
};

/**
 * Gets Template Icon svg.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {string} The annotation template icon or an empty string if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getIconSvg = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'iconSvg', '');
};

/**
 * Gets Template category of svg.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {string} The annotation template icon or an empty string if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getCategorySvg = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'categorySvg', '');
};

/**
 * Gets Template Tags.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {Array} The annotation template tags or an empty array if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getTags = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'tags', []);
};

/**
 * Gets Template isBase.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {Array} The annotation template tags or an empty array if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getIsBase = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'isBase', []);
};

/**
 * Gets Template template base id asociated to custom templates.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {Array} The annotation template tags or an empty array if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getTemplateBaseId = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'templateBaseId', []);
};

/**
 * Gets Template Elements array.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {Array} The annotation template array of elements or an empty array if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getElements = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  return get(annotationTemplateObject, 'elements', []);
};

/**
 * Gets the number of rows in the template.
 *
 * @param {object} annotationTemplateObject - The annotation template object.
 * @returns {number} The number of rows defined for that template or 0 if no rows are found.
 * @throws Error if annotation template object is undefined.
 */
export const getRows = annotationTemplateObject => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  let numberOfRows = 0;
  const annotationElements = getElements(annotationTemplateObject);
  forEach(annotationElements, element => {
    if (numberOfRows < element.row) numberOfRows = element.row;
  });
  return numberOfRows;
};

/**
 * Gets the number of columns in specific row.
 *
 * @param {object} arg - The parameters object.
 * @param {object} arg.annotationTemplateObject - The annotation template object.
 * @param {number} arg.rowNumber - The row to check for columns.
 * @returns {number} The number of columns defined for that row or 0 if no columns are found.
 * @throws Error if annotation template object is undefined.
 */
export const getColumnsForRow = ({ annotationTemplateObject, rowNumber }) => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  let numberOfColumns = 0;
  const annotationElements = getElements(annotationTemplateObject);
  forEach(annotationElements, element => {
    if (element.row === rowNumber && numberOfColumns < element.column)
      numberOfColumns = element.column;
  });
  return numberOfColumns;
};

/**
 * Gets the number of items in a specific column.
 *
 * @param {object} arg - The parameters object.
 * @param {object} arg.annotationTemplateObject - The annotation template object.
 * @param {number} arg.rowNumber - The row to check for columns.
 * @param {number} arg.columnNumber - The column to check for items.
 * @returns {number} The number of items defined for that column or 0 if no items are found.
 * @throws Error if annotation template object is undefined.
 */
export const getItemsInColumn = ({ annotationTemplateObject, rowNumber, columnNumber }) => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  let numberOfItemsInColumn = 0;
  const annotationElements = getElements(annotationTemplateObject);
  forEach(annotationElements, element => {
    if (
      element.row === rowNumber &&
      element.column === columnNumber &&
      numberOfItemsInColumn < element.order
    )
      numberOfItemsInColumn = element.order;
  });
  return numberOfItemsInColumn;
};

/**
 * Gets the template Element that has the rowNumber, columnNumber and itemNumber given.
 *
 * @param {object} arg - The parameters object.
 * @param {object} arg.annotationTemplateObject - The annotation template object.
 * @param {number} arg.rowNumber - The row of the item wanted.
 * @param {number} arg.columnNumber - The column of the item wanted.
 * @param {number} arg.itemNumber - The order of the item wanted.
 * @returns {object|undefined} The element in that position in the matrix or undefined if not found.
 * @throws Error if annotation template object is undefined.
 */
export const getElementItem = ({
  annotationTemplateObject,
  rowNumber,
  columnNumber,
  itemNumber
}) => {
  if (isNil(annotationTemplateObject)) throw new Error('annotation template not defined');
  const annotationElements = getElements(annotationTemplateObject);
  const selectedElement = find(
    annotationElements,
    element =>
      element.row === rowNumber && element.column === columnNumber && element.order === itemNumber
  );
  return selectedElement;
};

/**
 * Gets the row number for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {number} The row number for this element or 0 if no row is found.
 * @throws Error if element is empty or undefined.
 */
export const getElementRow = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'row', 0);
};

/**
 * Gets the column number for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {number} The column number for this element or 0 if no column is found.
 * @throws Error if element is empty or undefined.
 */
export const getElementColumn = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'column', 0);
};

/**
 * Gets the order number for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {number} The order number for this element or 0 if no order is found.
 * @throws Error if element is empty or undefined.
 */
export const getElementOrder = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'order', 0);
};

/**
 * Gets the label name for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The label for the input, options and radio types or an empty string if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementLabelName = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'labelName', '');
};

/**
 * Gets is show label name or not.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The label for the input, options and radio types or an empty string if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementisShowLabelName = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'isShowLabelName', false);
};

/**
 * Gets the value for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The value for the element or an empty string if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementValue = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'value', '');
};

/**
 * Gets the type for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The type for the element or 'input' if it's not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementType = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'elementType', 'input');
};

/**
 * Gets the data type for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The data type for the input, options and radio types or 'text' if it's not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementDataType = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'dataType', 'text');
};

/**
 * Gets the option list for the template element if select or radio.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {Array} The option list for select and radio types or an emtpy array if it's not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementOptionList = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'optionList', []);
};

/**
 * Transforms the optionlist array to a string of comma separated elements.
 *
 * @param {Array} optionList - The option list array for the element.
 * @returns {string} A comma separated string of the array elements or an empty string if not found.
 * @throws Error if optionList is undefined.
 */
export const transformOptionsArrayToString = optionList => {
  if (isNil(optionList)) throw new Error('No option list suplied');
  return join(optionList, ', ');
};

/**
 *  Transforms the optionlist string of comma separated elements to an array.
 *
 * @param {string} optionList - A comma separated string of the array elements.
 * @returns {Array} The option list array for the element.
 * @throws Error if optionList is empty or undefined.
 */
export const transformOptionsStringToArray = optionList => {
  if (isNil(optionList)) throw new Error('No optionList suplied');
  const re = /\s*,\s*/;
  let optionListArray = optionList.split(re);
  optionListArray = filter(optionListArray, option => !isEmpty(option));
  return optionListArray;
};

/**
 * Gets the element key for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The element key or an empty string if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementKey = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'elementKey', '');
};

/**
 * Gets the element caption for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The element caption or an empty string if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementCaption = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'caption', 'text');
};

/**
 * Gets the element caption for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The element caption or an empty string if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementIsShowCaption = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'isShowCaption', true);
};

/**
 * Gets the element icon key for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The element icon key or an empty string if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementIcon = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'iconElement', { library: 'fontawesome', icon: 'ad' });
};

/**
 * Gets the element height for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementHeight = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  let sizeDefault = 300;
  if (templateElementObject.elementType === 'button') sizeDefault = 70;
  return get(templateElementObject, 'height', sizeDefault);
};
/**
 * Gets the element width for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementWidth = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  let sizeDefault = 100;
  if (templateElementObject.elementType === 'button') sizeDefault = 10;
  return get(templateElementObject, 'width', sizeDefault);
};

/**
 * Gets the date template.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {boolean} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getDate = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'date', '');
};

/**
 * Gets the element hiperlink of image for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementHiperLink = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'hiperLink', '');
};

/**
 * Gets the element full screen environment of link for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {boolean} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElemenFullScreenEnv = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'fullScreenIpsilumEnv', true);
};

/**
 * Gets the element position of buttons to multiple templates.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {boolean} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElemenPositionMulti = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'positionMultiTemplates', 'flex-start');
};

/**
 * Gets the element position of buttons to multiple templates.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {boolean} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElemenPositionLabelName = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'positionLabelName', 'flex-start');
};

/**
 * Gets the element position of buttons to multiple templates.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {boolean} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElemenPosition = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'positionElement', 'flex-start');
};

/**
 * Gets the element show button to download file of templates.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {boolean} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementisDownload = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'isDownload', false);
};

/**
 * Gets the element show button to download file of templates.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {boolean} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getEnabledFullScreen = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'enabledFullScreen', true);
};

/**
 * Gets the element connections point cloud for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {object} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementConnections = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'connections', {});
};

/**
 * Gets the element map for the template element.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {string} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementMap = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'map', {});
};

/**
 * Gets the element multiplemaps.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {Array} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementMultipleMaps = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'multipleMaps', []);
};

/**
 * Gets the element multiValues.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {Array} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementMultiValues = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'multiValues', []);
};

/**
 * Gets the element multiplepoiintcloud.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {Array} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getElementMultiplePointCloud = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'multiplePointCloud', []);
};

/**
 * Gets are equal two elements of the same template.
 *
 * @param {object} templateElementObjectOne - An element from the array of template elements.
 * @param {object} templateElementObjectTwo - An element from the array of template elements.
 * @returns {boolean} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getAreEqualElements = (templateElementObjectOne, templateElementObjectTwo) => {
  if (isNil(templateElementObjectOne) || isNil(templateElementObjectTwo))
    throw new Error('No element suplied');
  const areEqualElements =
    isEqual(templateElementObjectOne.row, templateElementObjectTwo.row) &&
    isEqual(templateElementObjectOne.column, templateElementObjectTwo.column) &&
    isEqual(templateElementObjectOne.order, templateElementObjectTwo.order) &&
    isEqual(templateElementObjectOne.key, templateElementObjectTwo.key) &&
    isEqual(templateElementObjectOne.elementType, templateElementObjectTwo.elementType) &&
    isEqual(templateElementObjectOne.dataType, templateElementObjectTwo.dataType);
  return areEqualElements;
};

/**
 * Gets the element settings, mode edition or only view.
 *
 * @param {object} templateElementObject - An element from the array of template elements.
 * @returns {Array} The element height or 300 if not found.
 * @throws Error if element is empty or undefined.
 */
export const getSettingsElements = templateElementObject => {
  if (isNil(templateElementObject)) throw new Error('No element suplied');
  return get(templateElementObject, 'settings', []);
};
